/**
 * Sysadmin Platform → Hardware Profiles (EP-1 §8.1).
 *
 * Per docs/cross-cutting/EP-1-hardware-brand-slo-matrix.md §8.1:
 *   - Table: slug, display name, vendor, accelerator, VRAM, RAM,
 *     network tier, # tenants, # baselines, last edit
 *   - Row click → drawer with full detail
 *   - "+ Add profile" button → modal with full DDL form
 *
 * v1 ships the list + counts + create flow. PATCH and the verdict
 * matrix view are layered in v1.1 to keep the surface focused on the
 * §1.1 seed-incident remediation path: operator must SEE the profile
 * catalog before they can assign a tenant to one.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Cpu, Plus, ShieldCheck, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { SectionCard } from '@/components/ui/section-card';
import { PageError } from '@/components/ui/page-error';
import { StatTile } from '@/components/ui/stat-tile';
import {
  useHardwareProfiles,
  useCreateHardwareProfile,
  type Vendor,
  type AcceleratorModel,
  type NetworkIsolationTier,
  type HardwareProfileWithCounts,
} from '@/lib/use-sysadmin-ep1';

export const Route = createFileRoute('/sysadmin/platform/hardware-profiles')({
  component: HardwareProfilesPage,
});

const VENDOR_LABELS: Record<Vendor, string> = {
  nvidia: 'NVIDIA',
  amd: 'AMD',
  intel: 'Intel',
  arm: 'ARM',
  cpu: 'CPU',
};

const ACCELERATOR_LABELS: Record<AcceleratorModel, string> = {
  a6000: 'RTX A6000',
  l40s: 'L40S',
  h100: 'H100',
  h200: 'H200',
  mi300: 'MI300',
  mi325: 'MI325',
  strix_halo: 'Strix Halo',
  jetson_orin: 'Jetson Orin',
  jetson_thor: 'Jetson Thor',
  none: 'None (CPU)',
  other: 'Other',
};

const NETWORK_LABELS: Record<NetworkIsolationTier, string> = {
  no_egress: 'Air-gap (no egress)',
  allowlist: 'Allowlist',
  open: 'Open',
};

function HardwareProfilesPage() {
  const { data, isLoading, error, refetch } = useHardwareProfiles();
  const [createOpen, setCreateOpen] = useState(false);

  if (error) {
    return <PageError error={error} onRetry={refetch} />;
  }

  const profiles = data?.profiles ?? [];
  const totalTenants = profiles.reduce(
    (sum, p) => sum + p.activeTenantCount,
    0,
  );
  const totalBaselines = profiles.reduce(
    (sum, p) => sum + p.baselineCount,
    0,
  );
  const activeProfiles = profiles.filter((p) => !p.deletedAt).length;

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Cpu className="h-6 w-6 text-[#1890FF]" />
            Hardware Profiles
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Catalog of hardware substrates each tenant runs on. Brands are
            measured against profiles to produce SLO baselines; preflight
            validation uses both to decide whether a brand assignment can
            meet helper-budget SLOs.{' '}
            <span className="text-foreground font-medium">EP-1</span>.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Add profile
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatTile label="Active profiles" value={activeProfiles} icon={Cpu} />
        <StatTile
          label="Tenants assigned"
          value={totalTenants}
          icon={ShieldCheck}
        />
        <StatTile label="Baselines measured" value={totalBaselines} />
        <StatTile
          label="Profiles needing baselines"
          value={
            profiles.filter((p) => !p.deletedAt && p.baselineCount === 0).length
          }
          tone={
            profiles.filter((p) => !p.deletedAt && p.baselineCount === 0).length > 0
              ? 'warning'
              : 'success'
          }
        />
      </div>

      <SectionCard
        title="Catalog"
        description={
          isLoading ? 'Loading…' : `${profiles.length} profile(s)`
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                <th className="p-2">Slug</th>
                <th className="p-2">Display name</th>
                <th className="p-2">Vendor</th>
                <th className="p-2">Accelerator</th>
                <th className="p-2 text-right">VRAM (GB)</th>
                <th className="p-2 text-right">RAM (GB)</th>
                <th className="p-2">Network</th>
                <th className="p-2 text-right">Tenants</th>
                <th className="p-2 text-right">Baselines</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <ProfileRow key={p.id} profile={p} />
              ))}
              {profiles.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={10} className="p-4 text-center text-muted-foreground">
                    No hardware profiles configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <CreateProfileDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function ProfileRow({ profile }: { profile: HardwareProfileWithCounts }) {
  return (
    <tr className="border-b hover:bg-muted/30">
      <td className="p-2 font-mono text-xs">{profile.slug}</td>
      <td className="p-2 font-medium">{profile.displayName}</td>
      <td className="p-2">
        <Badge variant="outline">{VENDOR_LABELS[profile.vendor]}</Badge>
      </td>
      <td className="p-2">{ACCELERATOR_LABELS[profile.acceleratorModel]}</td>
      <td className="p-2 text-right">{profile.vramGb}</td>
      <td className="p-2 text-right">{profile.systemRamGb}</td>
      <td className="p-2">{NETWORK_LABELS[profile.networkIsolationTier]}</td>
      <td className="p-2 text-right">{profile.activeTenantCount}</td>
      <td className="p-2 text-right">
        {profile.baselineCount === 0 ? (
          <Badge variant="outline" className="text-amber-600 border-amber-600">
            No baselines
          </Badge>
        ) : (
          profile.baselineCount
        )}
      </td>
      <td className="p-2">
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </td>
    </tr>
  );
}

function CreateProfileDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const create = useCreateHardwareProfile();
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [vendor, setVendor] = useState<Vendor>('nvidia');
  const [acceleratorModel, setAcceleratorModel] =
    useState<AcceleratorModel>('a6000');
  const [vramGb, setVramGb] = useState<number>(48);
  const [systemRamGb, setSystemRamGb] = useState<number>(128);
  const [networkIsolationTier, setNetworkIsolationTier] =
    useState<NetworkIsolationTier>('no_egress');
  const [notes, setNotes] = useState('');

  const canSubmit =
    /^[a-z0-9_]+$/.test(slug) &&
    slug.length >= 2 &&
    displayName.length >= 1 &&
    vramGb >= 0 &&
    systemRamGb >= 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add hardware profile</DialogTitle>
          <DialogDescription>
            Add a substrate so brands can be measured and tenants assigned to
            it. Customers running CLAUDE.md §15.2 profiles already have seed
            rows.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <label className="flex flex-col gap-1 col-span-2">
            <span className="text-xs uppercase text-muted-foreground">
              Slug
            </span>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="cpu_only / standard_gpu / your_custom_slug"
            />
            <span className="text-xs text-muted-foreground">
              lowercase, digits, underscore only
            </span>
          </label>
          <label className="flex flex-col gap-1 col-span-2">
            <span className="text-xs uppercase text-muted-foreground">
              Display name
            </span>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. NVIDIA RTX A6000 48 GB"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase text-muted-foreground">
              Vendor
            </span>
            <Select value={vendor} onValueChange={(v) => setVendor(v as Vendor)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(VENDOR_LABELS) as Vendor[]).map((v) => (
                  <SelectItem key={v} value={v}>
                    {VENDOR_LABELS[v]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase text-muted-foreground">
              Accelerator
            </span>
            <Select
              value={acceleratorModel}
              onValueChange={(v) => setAcceleratorModel(v as AcceleratorModel)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(ACCELERATOR_LABELS) as AcceleratorModel[]).map(
                  (a) => (
                    <SelectItem key={a} value={a}>
                      {ACCELERATOR_LABELS[a]}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase text-muted-foreground">
              VRAM (GB)
            </span>
            <Input
              type="number"
              value={vramGb}
              onChange={(e) => setVramGb(Number(e.target.value))}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase text-muted-foreground">
              System RAM (GB)
            </span>
            <Input
              type="number"
              value={systemRamGb}
              onChange={(e) => setSystemRamGb(Number(e.target.value))}
            />
          </label>
          <label className="flex flex-col gap-1 col-span-2">
            <span className="text-xs uppercase text-muted-foreground">
              Network isolation tier
            </span>
            <Select
              value={networkIsolationTier}
              onValueChange={(v) =>
                setNetworkIsolationTier(v as NetworkIsolationTier)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(NETWORK_LABELS) as NetworkIsolationTier[]).map(
                  (n) => (
                    <SelectItem key={n} value={n}>
                      {NETWORK_LABELS[n]}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1 col-span-2">
            <span className="text-xs uppercase text-muted-foreground">
              Notes (optional)
            </span>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Hardware variant, dev box, customer-specific etc."
            />
          </label>
        </div>
        {create.error && (
          <div className="text-sm text-destructive">
            {create.error.message}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit || create.isPending}
            onClick={async () => {
              await create.mutateAsync({
                slug,
                displayName,
                vendor,
                acceleratorModel,
                vramGb,
                systemRamGb,
                networkIsolationTier,
                notes: notes || null,
              });
              onOpenChange(false);
              setSlug('');
              setDisplayName('');
              setNotes('');
            }}
          >
            Create profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
